/**
 * What the seed's caption says, and how big it is.
 *
 * The seed's label is the whole prompt: `startSession` writes the typed words
 * into it because a seed has no model behind it to name it (D90). For the
 * prompts people actually paste — a numbered brief, five paragraphs, a page —
 * that was a column of type on top of the first rings of the graph, and the
 * clamp-plus-"Show more" that replaced it was still three lines of truncated
 * prose, a glow, and a door, sitting where the eye lands first. The caption is
 * now a TITLE: the prompt's opening words, up to ten, the way a chapter is
 * named, with the whole prompt one click away on the prompt sheet
 * (`ui/PromptSheet.tsx`) and as the seed's card in the thread (D90). Nothing is stored: the title is a
 * pure function of the label, so it cannot drift from the words it names, and
 * every exploration ever saved gets one on load.
 *
 * Deterministic and local, not a model's: the caption is on screen the moment
 * the session starts, before any pass has run, and a demo or keyless canvas
 * has no model to ask. The cost accepted is that a heuristic title is plainer
 * than an authored one.
 *
 * Every size here mirrors `.nlabel[data-root='true']` in `styles/global.css`
 * and must stay in lockstep with it; `seedCaption.test.ts` holds the width.
 */

import { ringRadius } from '../core/layout'

/** The most words the title keeps, head and tail together. */
export const SEED_TITLE_MAX_WORDS = 10

/** `max-width` of `.nlabel[data-root='true']`, px. */
export const SEED_CAPTION_MAX_W = 240

/** Its horizontal padding, both sides together, px. */
export const SEED_CAPTION_PAD_X = 8

/** Average glyph advance of the display face at the title's 17.5px, px. */
export const SEED_CAPTION_CHAR_W = 8.9

/** One line of the title, px: 17.5px at its 1.2 line-height, rounded. */
export const SEED_CAPTION_LINE_H = 21

/** The "Full prompt" row under the title, px, including its top margin.
 *  Mirrors `.nlabel-expand`. */
export const SEED_CAPTION_EXPAND_H = 28

/** Its vertical padding, both edges together, px. */
export const SEED_CAPTION_PAD_Y = 4

/**
 * The seed caption's scale: in step with the ring round it.
 *
 * Every other caption keeps its type size at any zoom, which is right for a
 * label beside a dot. The seed's caption sits inside a ring that is drawn in
 * proportion to the camera, so the caption is too: its set size (17.5px) at
 * the zoom a Fit chooses for this graph on this stage (`fitCamera` in
 * `core/store.ts`), larger zoomed in and smaller zoomed out, between
 * `SEED_MIN_SCALE` and `SEED_MAX_SCALE` so it neither vanishes nor swamps the
 * graph. Relative to the Fit and not to an absolute zoom, because a Fit's zoom
 * falls as a graph grows: tied to one number, a large graph's title came out
 * at its floor at the very view meant to show it.
 *
 * And held inside the first ring: the rim is the circle round the caption's
 * box plus `SEED_AIR` (`seedRimFor`), and the title shrinks before that
 * circle would reach `SEED_RIM_MAX` of the way to the first ring's nodes.
 * Zoomed all the way out it used to stop at its floor while the rim went on
 * shrinking, and the words ran into the line; the renderer now never draws the
 * rim inside the caption (`seedClear`), so the floor can only push the rim
 * out, and this keeps that from swallowing the branches.
 *
 * `diag` is the caption's UNSCALED half-diagonal, px — half of the hypotenuse
 * of its measured box — or 0 when it is not yet known.
 */
export function seedScale(zoom: number, fitZoom: number, diag = 0): number {
  if (!Number.isFinite(zoom) || zoom <= 0 || !Number.isFinite(fitZoom) || fitZoom <= 0) return 1
  let s = zoom / fitZoom
  if (diag > 0) s = Math.min(s, (ringRadius(1) * SEED_RIM_MAX * zoom - SEED_AIR) / diag)
  return Math.min(SEED_MAX_SCALE, Math.max(SEED_MIN_SCALE, s))
}

/** The rim that clears a caption whose on-screen ink box is `w`×`h` px at
 *  camera scale `scale`: the circle through its corners, plus `SEED_AIR` of
 *  space between words and line, scaled with the words — zoomed out, a fixed
 *  margin round a small title was most of the rim. */
export function seedRimFor(w: number, h: number, scale = 1): number {
  return Math.hypot(w / 2, h / 2) + SEED_AIR * scale
}
/** The furthest the seed's rim reaches, screen px: `SEED_RING_CAP` of the way
 *  to the first ring, so the outer part of every first-ring strand is drawn. */
export function seedRingCap(zoom: number): number {
  return ringRadius(1) * SEED_RING_CAP * zoom
}

/** Smallest and largest the seed caption is drawn: about 10px and 35px type. */
export const SEED_MIN_SCALE = 0.55
export const SEED_MAX_SCALE = 2

/** Space between the caption's box and the rim, screen px. */
export const SEED_AIR = 14

/** The furthest the rim may reach toward the first ring's nodes, as a share of
 *  that ring's radius, before the title shrinks instead: short of the nodes by
 *  more than their jitter, so every first-ring strand still shows a stem. */
export const SEED_RIM_MAX = 0.86

/** The share of the first ring's radius a rim round a SMALL caption is held
 *  to; a larger one is drawn round the caption instead (`seedClear`). */
export const SEED_RING_CAP = 0.72

/** Words a cut title must not end on — "Polynomial-time SAT via" reads broken. */
const DANGLING = new Set(
  'a an and as at but by for from in into of on or the to via vs with without through that which whether if than then is are be'.split(
    ' ',
  ),
)

/** Where a title may break into a bold head and a quieter tail: before one of these. */
const HINGE = new Set(
  'via by with without through using from for in under against versus vs beyond'.split(' '),
)

export interface SeedTitle {
  head: string
  tail: string
  /** True when the title is not the whole prompt — it then ends in an
   *  ellipsis where words were cut. */
  cut: boolean
}

/**
 * The prompt's opening words, as a title: `head` in full weight, and `tail` —
 * a trailing "via …" or "with …" phrase — set quieter under it, the way a
 * subtitle is. `tail` is empty when there is no hinge to break at.
 *
 * A PREFIX of the prompt, never a paraphrase: "Is it possible to build an
 * antigravity device?" asks something "Build an antigravity device" does not,
 * so no word is dropped from the front or the middle and the question mark
 * stays. Only formatting goes (heading, list and emphasis marks). The first
 * line and sentence, then at most `SEED_TITLE_MAX_WORDS` words, cut back off
 * any word that would leave it dangling, with an ellipsis where words were cut.
 */
export function seedTitle(prompt: string): SeedTitle {
  const whole = prompt.trim()
  let s = (whole.split(/\n/).find((l) => l.trim()) ?? '').trim()
  s = s.replace(/^(?:[#>*\-•]+|\d+[.)])\s*/, '').replace(/[*_`]/g, '')
  // First sentence, its end mark kept. A full stop inside "e.g." or "3.5" is
  // not one: it must be followed by a space and a capital, or the end.
  const end = s.search(/[.?!](?=\s+[A-Z"“(]|\s*$)/)
  if (end >= 0) s = s.slice(0, end + 1)

  let words = s.split(/\s+/).filter(Boolean)
  if (!words.length) return { head: whole.slice(0, 60), tail: '', cut: whole.length > 60 }
  let cut = false
  if (words.length > SEED_TITLE_MAX_WORDS) {
    words = words.slice(0, SEED_TITLE_MAX_WORDS)
    cut = true
    while (words.length > 1 && DANGLING.has(bare(words[words.length - 1]!))) words.pop()
    words[words.length - 1] = words[words.length - 1]!.replace(/[,;:]+$/, '')
  }
  words[0] = words[0]!.charAt(0).toUpperCase() + words[0]!.slice(1)
  // Cut short of the prompt's end anywhere — a later sentence, a second line
  // — is still a cut, and the caption says so with its door.
  if (!cut && words.join(' ').length < whole.replace(/\s+/g, ' ').length - 2) cut = true

  // The hinge: the first one with at least two words on either side of it.
  let at = -1
  for (let i = 2; i <= words.length - 2; i++) {
    if (HINGE.has(bare(words[i]!))) {
      at = i
      break
    }
  }
  const ell = cut && !/[.?!]$/.test(words[words.length - 1]!) ? '…' : ''
  if (at < 0) return { head: words.join(' ') + ell, tail: '', cut }
  return { head: words.slice(0, at).join(' '), tail: words.slice(at).join(' ') + ell, cut }
}

function bare(w: string): string {
  return w.toLowerCase().replace(/[^\p{L}\p{N}'-]/gu, '')
}

function lines(text: string): number {
  if (!text) return 0
  return Math.max(
    1,
    Math.ceil((text.length * SEED_CAPTION_CHAR_W) / (SEED_CAPTION_MAX_W - SEED_CAPTION_PAD_X)),
  )
}

/**
 * The caption's estimated box, `[width, height]` in px, for the frames before
 * a real measurement exists. The halo bleed and hit padding the caption layer
 * adds to every measured box are added by the caller, so this is the box the
 * browser would report — which is what makes the two comparable.
 */
export function seedCaptionBox(label: string): [number, number] {
  const { head, tail } = seedTitle(label)
  const longest = Math.max(head.length, tail.length, 4)
  const w = Math.min(SEED_CAPTION_MAX_W, longest * SEED_CAPTION_CHAR_W + SEED_CAPTION_PAD_X)
  const h =
    (lines(head) + lines(tail)) * SEED_CAPTION_LINE_H + SEED_CAPTION_EXPAND_H + SEED_CAPTION_PAD_Y
  return [w, h]
}
